'use client';

import { useCallback, useState } from 'react';

import { cn } from '@ui/lib/utils';

import StarFilled from '../../../icons/StarFilled';
import StarOutline from '../../../icons/StarOutline';

export interface StarRatingProps {
  value?: number;
  onChange?: (value: number) => void;
  size?: 'sm' | 'md' | 'lg';
  readonly?: boolean;
  className?: string;
  showValue?: boolean;
  emptyColor?: string;
}

const sizeMap = {
  sm: 'size-4',
  md: 'size-6',
  lg: 'size-8'
} as const;

export const StarRating = ({
  value = 0,
  onChange,
  size = 'md',
  readonly = false,
  className,
  showValue,
  emptyColor
}: StarRatingProps) => {
  const [hovered, setHovered] = useState<number | null>(null);
  const isInteractive = !readonly && !!onChange;
  const displayValue = hovered ?? value;

  const handleClick = useCallback(
    (star: number) => {
      if (isInteractive) onChange(star);
    },
    [isInteractive, onChange]
  );

  return (
    <div
      className={cn('inline-flex items-center gap-2', className)}
      role={isInteractive ? 'radiogroup' : 'img'}
      aria-label={`Rating: ${value} out of 5 stars`}
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const fillFraction = Math.min(1, Math.max(0, displayValue - (star - 1)));
        const isFullyFilled = fillFraction >= 1;
        const isPartial = fillFraction > 0 && fillFraction < 1;

        return (
          <button
            key={star}
            type="button"
            disabled={!isInteractive}
            className={cn(
              'shrink-0 transition-colors disabled:cursor-default',
              isInteractive && 'cursor-pointer hover:scale-110 transition-transform'
            )}
            onClick={() => handleClick(star)}
            onMouseEnter={() => isInteractive && setHovered(star)}
            onMouseLeave={() => isInteractive && setHovered(null)}
            aria-label={`${star} star${star > 1 ? 's' : ''}`}
          >
            {isPartial && readonly ? (
              <span className={cn('relative block', sizeMap[size])}>
                <StarOutline className={cn('absolute inset-0', sizeMap[size], emptyColor ?? 'text-neutral-300')} />
                <span className="absolute inset-0 overflow-hidden" style={{ width: `${fillFraction * 100}%` }}>
                  <StarFilled className={cn(sizeMap[size], 'text-[#FDC812]')} />
                </span>
              </span>
            ) : (
              <span className={cn(isFullyFilled ? 'text-[#FDC812]' : (emptyColor ?? 'text-neutral-300'))}>
                {isFullyFilled ? <StarFilled className={sizeMap[size]} /> : <StarOutline className={sizeMap[size]} />}
              </span>
            )}
          </button>
        );
      })}
      {showValue && <span className="ml-1 text-sm font-medium text-neutral-700">{value.toFixed(1)}</span>}
    </div>
  );
};
